import React from 'react';
import { ResponsiveLine } from '@nivo/line';
import { ResponsiveBar } from '@nivo/bar';
import { ResponsivePie } from '@nivo/pie';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  useTheme,
  useMediaQuery,
  Button,
  Stack,
  Avatar,
  LinearProgress
} from '@mui/material';
import {
  TrendingUp,
  AttachMoney,
  ShoppingCart,
  LocationOn,
  Receipt,
  Assessment,
  People,
  Timeline,
  PieChart,
  TableChart,
  AccountBalance
} from '@mui/icons-material';
import GenericModal from '../GenericModal';
import { useDataFormatters } from '../../Hooks/useDataFormatters';
import { formatYAxisValue, formatHorizontalBarLabel } from '../DrillDown/formatters';
import { DetailedAnalysisSkeleton } from '../Skeletons/ModalSkeletons';

interface ProdutoVendasDrillDownModalProps {
  open: boolean;
  onClose: () => void;
  data: any;
  loading: boolean;
  selectedProduto: string | null;
  error: string | null;
  onRetry: () => void;
}

const ProdutoVendasDrillDownModal: React.FC<ProdutoVendasDrillDownModalProps> = ({
  open,
  onClose,
  data,
  loading,
  selectedProduto,
  error,
  onRetry
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { formatCurrency, formatNumber, formatQuantity, formatMonthYear, formatDate } = useDataFormatters();

  const renderContent = () => {
    if (!data || !data.produto) return null;

    const { produto, vendas_por_mes, top_cfops, top_clientes, top_regioes, transacoes_detalhadas } = data;



    // Dados para gráfico de evolução de vendas
    const evolucaoData = [
      {
        id: 'Vendas',
        data: vendas_por_mes.map((item: any) => ({
          x: formatMonthYear(item.mes, item.ano),
          y: item.valor_vendas
        }))
      }
    ];

    // Dados para gráfico de CFOPs
    const cfopsData = top_cfops.map((item: any) => ({
      id: formatHorizontalBarLabel(`CFOP ${item.cfop}`, 20),
      label: formatHorizontalBarLabel(`CFOP ${item.cfop}`, 20),
      value: item.valor_total
    }));



    return (
      <Box sx={{ p: 0 }}>


        {/* Header com informações principais */}
        <Card sx={{ mb: 3, background: `linear-gradient(135deg, ${theme.palette.primary.main}15, ${theme.palette.secondary.main}15)` }}>
          <CardContent>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={8}>
                <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                  <Avatar sx={{ bgcolor: theme.palette.primary.main, width: 56, height: 56 }}>
                    <Assessment sx={{ fontSize: 28 }} />
                  </Avatar>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                      {produto.descr_compl}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Código: {produto.cod_item}
                    </Typography>
                  </Box>
                </Stack>
              </Grid>
              <Grid item xs={12} md={4}>
                <Stack spacing={1}>
                  <Chip 
                    icon={<AttachMoney />} 
                    label={`Participação: ${produto.participacao_percentual.toFixed(2)}%`}
                    color="primary"
                    variant="filled"
                    sx={{ fontWeight: 'bold' }}
                  />
                  <Chip 
                    icon={<ShoppingCart />} 
                    label={`${formatQuantity(produto.quantidade_total)} unidades`}
                    color="secondary"
                    variant="filled"
                  />
                </Stack>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Cards de métricas principais */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={6} md={3}>
            <Card sx={{ height: '100%', textAlign: 'center' }}>
              <CardContent>
                <AttachMoney sx={{ fontSize: 40, color: theme.palette.success.main, mb: 1 }} />
                <Typography variant="h6" color="success.main" sx={{ fontWeight: 'bold' }}>
                  {formatCurrency(produto.valor_total_vendas)}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Vendas Totais
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card sx={{ height: '100%', textAlign: 'center' }}>
              <CardContent>
                <Receipt sx={{ fontSize: 40, color: theme.palette.info.main, mb: 1 }} />
                <Typography variant="h6" color="info.main" sx={{ fontWeight: 'bold' }}>
                  {formatNumber(produto.total_transacoes)}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Transações
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card sx={{ height: '100%', textAlign: 'center' }}>
              <CardContent>
                <TrendingUp sx={{ fontSize: 40, color: theme.palette.warning.main, mb: 1 }} />
                <Typography variant="h6" color="warning.main" sx={{ fontWeight: 'bold' }}>
                  {formatCurrency(produto.valor_medio_transacao)}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Ticket Médio
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card sx={{ height: '100%', textAlign: 'center' }}>
              <CardContent>
                <AccountBalance sx={{ fontSize: 40, color: theme.palette.error.main, mb: 1 }} />
                <Typography variant="h6" color="error.main" sx={{ fontWeight: 'bold' }}>
                  {formatCurrency(produto.total_debito_icms)}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  ICMS Débito
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Gráficos principais */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          {/* Evolução de Vendas */}
          <Grid item xs={12} lg={8}>
            <Card sx={{ height: 400 }}>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                  <Timeline sx={{ color: theme.palette.primary.main }} />
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    Evolução de Vendas
                  </Typography>
                </Stack>
                <Box sx={{ height: 320 }}>
                  <ResponsiveLine
                    data={evolucaoData}
                    margin={{ top: 20, right: 20, bottom: 90, left: 100 }}
                    xScale={{ type: 'point' }}
                    yScale={{ type: 'linear', min: 'auto', max: 'auto' }}
                    axisTop={null}
                    axisRight={null}
                    axisBottom={{
                      tickSize: 5,
                      tickPadding: 5,
                      tickRotation: -45,
                      legend: 'Período',
                      legendOffset: 60,
                      legendPosition: 'middle'
                    }}
                    axisLeft={{
                      tickSize: 5,
                      tickPadding: 5,
                      tickRotation: 0,
                      legend: 'Valor (R$)',
                      legendOffset: -95,
                      legendPosition: 'middle',
                      format: (value) => formatCurrency(value)
                    }}
                    pointSize={10}
                    pointColor={{ theme: 'background' }}
                    pointBorderWidth={2}
                    pointBorderColor={{ from: 'serieColor' }}
                    pointLabelYOffset={-12}
                    useMesh={true}
                    colors={[theme.palette.primary.main]}
                    lineWidth={3}
                    enableArea={true}
                    areaOpacity={0.1}
                    legends={[
                      {
                        anchor: 'top',
                        direction: 'row',
                        justify: false,
                        translateX: 0,
                        translateY: -10,
                        itemsSpacing: 40, // Espaçamento equilibrado
                        itemDirection: 'left-to-right',
                        itemWidth: 100, // Largura compacta (símbolo + texto próximos)
                        itemHeight: 20,
                        itemOpacity: 0.75,
                        symbolSize: 12,
                        symbolShape: 'circle',
                        symbolBorderColor: 'rgba(0, 0, 0, .5)',
                        effects: [
                          {
                            on: 'hover',
                            style: {
                              itemBackground: 'rgba(0, 0, 0, .03)',
                              itemOpacity: 1
                            }
                          }
                        ]
                      }
                    ]}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Top CFOPs */}
          <Grid item xs={12} lg={4}>
            <Card sx={{ height: 400 }}>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                  <PieChart sx={{ color: theme.palette.secondary.main }} />
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    Top CFOPs
                  </Typography>
                </Stack>
                <Box sx={{ height: 320 }}>
                  <ResponsivePie
                    data={cfopsData}
                    margin={{ top: 40, right: 100, bottom: 40, left: 100 }}
                    innerRadius={0.5}
                    padAngle={0.7}
                    cornerRadius={3}
                    activeOuterRadiusOffset={8}
                    borderWidth={1}
                    borderColor={{ from: 'color', modifiers: [['darker', 0.2]] }}
                    arcLinkLabelsSkipAngle={10}
                    arcLinkLabelsTextColor="#333333"
                    arcLinkLabelsThickness={2}
                    arcLinkLabelsColor={{ from: 'color' }}
                    arcLabelsSkipAngle={10}
                    arcLabelsTextColor={{ from: 'color', modifiers: [['darker', 2]] }}
                    colors={{ scheme: 'category10' }}
                    legends={[
                      {
                        anchor: 'bottom',
                        direction: 'row',
                        justify: false,
                        translateX: 0,
                        translateY: 56,
                        itemsSpacing: 50, // Espaçamento equilibrado
                        itemWidth: 120, // Largura compacta (símbolo + texto próximos)
                        itemHeight: 18,
                        itemTextColor: '#999',
                        itemDirection: 'left-to-right',
                        itemOpacity: 1,
                        symbolSize: 18,
                        symbolShape: 'circle',
                        effects: [
                          {
                            on: 'hover',
                            style: {
                              itemTextColor: '#000'
                            }
                          }
                        ]
                      }
                    ]}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Análise de Clientes e Regiões */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          {/* Top Clientes */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                  <People sx={{ color: theme.palette.info.main }} />
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    Top 10 Clientes
                  </Typography>
                </Stack>
                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>Cliente</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>Vendas</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>Qtd</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>Ticket</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {top_clientes.slice(0, 10).map((cliente: any, index: number) => (
                        <TableRow key={index} hover>
                          <TableCell>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                {cliente.nome.length > 25 ? `${cliente.nome.substring(0, 25)}...` : cliente.nome}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                {cliente.uf} - {cliente.municipio}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" sx={{ fontWeight: 'bold', color: theme.palette.success.main }}>
                              {formatCurrency(cliente.total_vendas)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            {formatQuantity(cliente.quantidade_vendida)}
                          </TableCell>
                          <TableCell align="right">
                            {formatCurrency(cliente.ticket_medio)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* Vendas por Região */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                  <LocationOn sx={{ color: theme.palette.warning.main }} />
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    Vendas por Região
                  </Typography>
                </Stack>
                <Box sx={{ height: 300 }}>
                  {top_regioes && Array.isArray(top_regioes) && top_regioes.length > 0 ? (
                    <ResponsiveBar
                      data={top_regioes.map((item: any) => ({
                        uf: item.uf || 'N/D',
                        valor: item.valor_total || 0,
                        transacoes: item.total_transacoes || 0,
                        clientes: item.total_clientes || 0
                      }))}
                      keys={['valor']}
                      indexBy="uf"
                      margin={{ top: 20, right: 20, bottom: 50, left: 100}}
                      padding={0.3}
                      valueScale={{ type: 'linear' }}
                      indexScale={{ type: 'band', round: true }}
                      colors={[theme.palette.warning.main]}
                      borderColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
                      axisTop={null}
                      axisRight={null}
                      axisBottom={{
                        tickSize: 5,
                        tickPadding: 5,
                        tickRotation: 0,
                        legend: 'UF',
                        legendPosition: 'middle',
                        legendOffset: 32
                      }}
                      axisLeft={{
                        tickSize: 5,
                        tickPadding: 5,
                        tickRotation: 0,
                        legend: 'Valor (R$)',
                        legendPosition: 'middle',
                        legendOffset: -90,
                        format: (value) => formatYAxisValue(value as number)
                      }}
                      labelSkipWidth={12}
                      labelSkipHeight={12}
                      labelTextColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
                      enableLabel={false}
                    />
                  ) : (
                    <Box 
                      sx={{ 
                        height: '100%', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        color: 'text.secondary'
                      }}
                    >
                      <Typography variant="body2">
                        Nenhum dado de região disponível para este produto
                      </Typography>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Transações Detalhadas */}
        <Card>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <TableChart sx={{ color: theme.palette.primary.main }} />
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                Últimas Transações
              </Typography>
            </Stack>
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Data</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Documento</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>CFOP</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Cliente</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Valor</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>ICMS</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Qtd</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Preço Unit.</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {transacoes_detalhadas.map((transacao: any, index: number) => (
                    <TableRow key={index} hover>
                      <TableCell>{formatDate(transacao.dt_emissao)}</TableCell>
                      <TableCell>{transacao.num_doc}</TableCell>
                      <TableCell>
                        <Chip 
                          label={transacao.cfop} 
                          size="small" 
                          variant="outlined"
                          color="primary"
                        />
                      </TableCell>
                      <TableCell>
                        {transacao.cliente.length > 20 ? `${transacao.cliente.substring(0, 20)}...` : transacao.cliente}
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: theme.palette.success.main }}>
                          {formatCurrency(transacao.vl_item)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="error.main">
                          {formatCurrency(transacao.vl_icms)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {formatQuantity(transacao.qtd)}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(transacao.preco_unitario)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Box>
    );
  };

  return (
    <GenericModal
      open={open}
      onClose={onClose}
      title="Análise Detalhada - Produto"
      loading={false}
      showBreadcrumb={true}
      breadcrumbItems={[{ label: selectedProduto || 'Produto' }]}
      maxWidth="xl"
      fullWidth
    >
      {loading ? (
        <DetailedAnalysisSkeleton />
      ) : error ? (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="error" variant="h6" gutterBottom>
            Erro ao carregar dados
          </Typography>
          <Typography color="textSecondary" sx={{ mb: 2 }}>
            {error}
          </Typography>
          <Button onClick={onRetry} variant="contained" color="primary" size="large">
            Tentar Novamente
          </Button>
        </Box>
      ) : (
        renderContent()
      )}
    </GenericModal>
  );
};

export default ProdutoVendasDrillDownModal; 